// src/lib/chartScale.js

/**
 * Chart scale utilities (Recharts-friendly)
 * ------------------------------------------------------------
 * Exports:
 *  - makeNiceScale(min, max, maxTicks?): general scale, supports negatives
 *  - makeNiceScaleZeroBased(max, maxTicks?, padSteps?): clamps min to 0, adds headroom
 *
 * Returned shape:
 *  { niceMin, niceMax, ticks, step }
 */

/* ---------- Core helpers ---------- */

function niceNum(range, round) {
  const exponent = Math.floor(Math.log10(range || 1));
  const fraction = range / Math.pow(10, exponent);
  let niceFraction;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }

  return niceFraction * Math.pow(10, exponent);
}

function tidyTick(v, step) {
  // Avoid floating artifacts (e.g., 0.30000000000004)
  if (!Number.isFinite(v)) return v;
  if (!Number.isFinite(step) || step === 0) return v;

  if (Math.abs(step) >= 1) return Math.round(v);

  // Keep plenty of precision, then strip noise.
  const fixed = Number(v.toFixed(10));
  return fixed === 0 ? 0 : fixed;
}

function makeNiceScaleCore(min, max, maxTicks = 6) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    const base = Number.isFinite(min) ? min : 0;
    const span = Math.max(Math.abs(base) * 0.2, 1);
    return makeNiceScaleCore(base - span, base + span, maxTicks);
  }

  const range = niceNum(max - min, false);
  const step = niceNum(range / Math.max(maxTicks - 1, 2), true);

  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;

  const ticks = [];
  for (let v = niceMin; v <= niceMax + 1e-9; v += step) {
    ticks.push(tidyTick(v, step));
  }

  return { niceMin, niceMax, ticks, step };
}

/* ---------- Public exports ---------- */

/**
 * General nice scale, supports negatives and mixed-sign ranges.
 */
export function makeNiceScale(min, max, maxTicks = 6) {
  return makeNiceScaleCore(min, max, maxTicks);
}

/**
 * Zero-based nice scale (min = 0), intended for non-negative money series.
 * Adds optional headroom via padSteps (defaults to 1 to match current DS/FundPlanner behavior).
 */
export function makeNiceScaleZeroBased(max, maxTicks = 6, padSteps = 1) {
  const baseMax = Number.isFinite(max) ? max : 0;
  const safeMax = Math.max(1, baseMax);

  // Compute step from the unpadded range.
  const core = makeNiceScaleCore(0, safeMax, maxTicks);
  const step = core.step || 1;

  const niceMin = 0;
  const niceMax = Math.ceil(safeMax / step) * step + Math.max(0, padSteps) * step;

  const ticks = [];
  for (let v = niceMin; v <= niceMax + 1e-9; v += step) {
    ticks.push(tidyTick(v, step));
  }

  return { niceMin, niceMax, ticks, step };
}
